import { EventBookingDetails } from '@/modals/event-details/eventBookingDetails';
import { FeedbackDetails } from '@/modals/feedbackDetails';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-feedback-details',
  templateUrl: './feedback-details.component.html',
  styleUrl: './feedback-details.component.scss'
})
export class FeedbackDetailsComponent implements OnInit {
  destroy$: Subject<boolean> = new Subject<boolean>();
  searchText: any;
  eventDetails: EventBookingDetails[] = [];
  currentEventDetails: EventBookingDetails;
  eventId: number = 0;
  getListOfFeedbackDetails: FeedbackDetails[] = [];


  constructor(
    private appService: AppService, private router: Router,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
  ) {
  }

  ngOnInit(): void {
    this.eventDetailsGetAll();
  }

  eventDetailsGetAll() {
    this.spinner.show();
    this.appService.eventDetailsGetAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        const today = new Date().setHours(0, 0, 0, 0);
        const sorted = data
          .sort((a, b) => {
            const aIsFuture = a.startDate >= today;
            const bIsFuture = b.startDate >= today;
            if (aIsFuture && !bIsFuture) return -1;
            if (!aIsFuture && bIsFuture) return 1;
            return a.startDate - b.startDate;
          });
        this.eventDetails = sorted;
        this.spinner.hide();
      }, (error: any) => {
        this.spinner.hide()
        this.toastr.error(error?.error?.message || "Something Went Wrong, Please Try Again")
      });
  }

  selectValue(event: any) {
    this.spinner.show();
    const eventId = +event.target.value;
    this.eventId = Number(eventId);
    this.currentEventDetails = this.eventDetails.find(item => item.eventId == eventId)
    this.getEventIdWorkshopDetails(this.eventId)
    this.spinner.hide();
  }

  showThankYouMail(event: EventBookingDetails): boolean {
    if (!event || !event.startDate || !event.startTime) return false;
    return this.isRegistrationClosed(event);
  }

  formattedDate(epoch: number): string {
    const date = new Date(epoch);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  isRegistrationClosed(event: any): boolean {
    const now = Date.now();

    // Convert epoch startDate to real Date object
    const startDate = new Date(event.startDate);

    // Validate time string
    if (!event.startTime || !event.startTime.includes(' ')) return false;

    const [timePart, meridian] = event.startTime.trim().split(' ');
    let [hours, minutes] = timePart.split('.').map(Number);

    if (isNaN(hours)) return false;
    minutes = isNaN(minutes) ? 0 : minutes;

    // Convert to 24-hour format
    if (meridian.toUpperCase() === 'PM' && hours < 12) hours += 12;
    if (meridian.toUpperCase() === 'AM' && hours === 12) hours = 0;

    // Set hours/minutes on startDate
    startDate.setHours(hours, minutes, 0, 0);

    // Now compare
    return now > startDate.getTime();
  }


  getEventIdWorkshopDetails(eventId: number) {
    this.spinner.show();
    this.appService.getEventIdFeedback(eventId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        const sorted = data.sort((a, b) => b.feedbackDetailsSeq - a.feedbackDetailsSeq);
        this.getListOfFeedbackDetails = sorted;
        this.spinner.hide();
      }, (error: any) => {
        this.spinner.hide();
        this.toastr.error(error.error.message || "Something Went Wrong,Please Try Again....")
      })
  }

  generateExcelSheet(): void {

  if (!this.getListOfFeedbackDetails?.length) {
    this.toastr.error('No data found to download!');
    return;
  }

  // 1️⃣ Prepare Excel Data
  const excelData = this.getListOfFeedbackDetails.map((item, index) => ({
    'S.No': index + 1,
    'Name': item.name || '-',
    'City': item.city || '-',
    'Email': item.email || '-',
    'Overall Experience': item.overallExperience || '-',
    'Most Liked': item.mostLiked || '-',
    'Improvements Needed': item.improvements || '-',
    'Best Team / Section': item.bestTeamOrSection || '-',
    'Will Participate Again': item.willParticipateAgain || '-',
    'Additional Comments': item.additionalComments || '-',
    'Created Date': item.createdAt
      ? new Date(item.createdAt).toLocaleDateString()
      : '-'
  }));

  // 2️⃣ Create Worksheet
  const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(excelData);

  // 3️⃣ Header Style
  const headerStyle = {
    font: { bold: true },
    fill: { patternType: 'solid', fgColor: { rgb: 'CCE5FF' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true }
  };

  const defaultCenterStyle = {
    alignment: {
      wrapText: true,
      vertical: 'center',
      horizontal: 'center'
    }
  };

  const leftWrapStyle = {
    alignment: {
      wrapText: true,
      vertical: 'center',
      horizontal: 'left'
    }
  };

  // 4️⃣ Column Widths
  ws['!cols'] = [
    { wch: 6 },   // S.No
    { wch: 20 },  // Name
    { wch: 18 },  // City
    { wch: 25 },  // Email
    { wch: 25 },  // Overall Experience
    { wch: 30 },  // Most Liked
    { wch: 30 },  // Improvements
    { wch: 25 },  // Best Team
    { wch: 22 },  // Participate Again
    { wch: 40 },  // Additional Comments
    { wch: 18 }   // Created Date
  ];

  // 5️⃣ Row Heights (dynamic for comments)
  const rowHeights = excelData.map(row => {
    const lines =
      (String(row['Additional Comments']).match(/\n/g) || []).length + 1;
    return { hpx: Math.max(22, lines * 22) };
  });

  ws['!rows'] = [{ hpx: 30 }, ...rowHeights];

  // 6️⃣ Apply Styles
  const range = XLSX.utils.decode_range(ws['!ref']!);

  for (let c = range.s.c; c <= range.e.c; c++) {

    const headerCell = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[headerCell]) ws[headerCell].s = headerStyle;

    for (let r = range.s.r + 1; r <= range.e.r; r++) {

      const cellAddr = XLSX.utils.encode_cell({ r, c });
      const cell = ws[cellAddr];
      if (!cell) continue;

      const headerName = ws[headerCell]?.v;

      if (
        headerName === 'Most Liked' ||
        headerName === 'Improvements Needed' ||
        headerName === 'Additional Comments'
      ) {
        cell.s = leftWrapStyle;
      } else {
        cell.s = defaultCenterStyle;
      }
    }
  }

  // 7️⃣ Generate & Download Excel
  const wb: XLSX.WorkBook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Feedback Details');

  const wbout: ArrayBuffer = XLSX.write(wb, {
    bookType: 'xlsx',
    type: 'array'
  });

  saveAs(
    new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }),
    'Feedback_Details.xlsx'
  );
}


}
